import React, { useEffect, useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import { getAPIBaseURL, getUser } from "../services/helpers"
import { getData, postData } from "../services/request"
import DisputeForm from "../components/DisputeForm"

const HospitalRequestDetails = () => {
  const { postId } = useParams()
  const [request, setRequest] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [price, setPrice] = useState("")
  const [comment, setComment] = useState("")
  const [btnState, setBtnState] = useState("Submit price")
  const [genMsg, setGenMsg] = useState("")
  const [delivering, setDelivering] = useState(false)
  const [disputes, setDisputes] = useState([])
  const [loadingDisputes, setLoadingDisputes] = useState(true)
  const user = getUser()

  const load = async () => {
    setLoading(true)
    setError("")
    const url = `${getAPIBaseURL()}/v1/posts/${postId}`
    const res = await getData(url, {}, {})
    setLoading(false)
    if(res.error == ""){
      if(res.result.status === 200){
        setRequest(res.result.data?.data || null)
      }else{
        setError(res.result.data?.message || 'Could not load request')
      }
    }else{
      setError(res.result?.data?.message || 'Network error')
    }
  }

  const loadDisputes = async () => {
    setLoadingDisputes(true)
    try {
      const url = `${getAPIBaseURL()}/v1/disputes`
      const params = { limit: 200, skip: 0 }
      const res = await getData(url, {}, params)
      if (res.error == "") {
        if (res.result.status === 200) {
          const all = res.result.data?.data || []
          const filtered = all.filter((d) => {
            const dpId = d.postId?._id || d.postId
            return dpId && String(dpId) === String(postId)
          })
          setDisputes(filtered)
        } else {
          setDisputes([])
        }
      } else {
        setDisputes([])
      }
    } finally {
      setLoadingDisputes(false)
    }
  }

  useEffect(() => {
    load()
    loadDisputes()
  }, [postId])

  const myPrice = useMemo(() => {
    const myId = user?._id || user?.id
    const prices = request?.prices || []
    return prices.find((p) => {
      const hid = typeof p.userId === 'string' ? p.userId : (p.userId?._id || p.userId?.id)
      return hid === myId
    })
  }, [request, user])

  const submitPrice = async () => {
    setGenMsg("")
    setError("")
    if(!price || isNaN(Number(price)) || Number(price) <= 0){
      setError('Enter a valid positive price')
      return
    }
    setBtnState("Submitting...")
    const url = `${getAPIBaseURL()}/v1/posts/add-price`
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' }
    const params = new URLSearchParams()
    params.append('postId', postId)
    params.append('price', price)
    if(comment) params.append('comment', comment)
    const res = await postData(url, headers, params.toString())
    setBtnState("Submit price")
    if(res.error == ""){
      if(res.result.status === 200){
        setGenMsg(res.result.data?.message || 'Price submitted')
        setPrice("")
        setComment("")
        load()
      }else{
        setError(res.result.data?.message || 'Could not submit price')
      }
    }else{
      setError(res.result?.data?.message || 'Network error')
    }
  }

  const handleMarkDelivered = async () => {
    setGenMsg("")
    setError("")
    setDelivering(true)
    const url = `${getAPIBaseURL()}/v1/posts/mark-delivered`
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' }
    const params = new URLSearchParams()
    params.append('postId', postId)
    const res = await postData(url, headers, params.toString())
    setDelivering(false)
    if(res.error == ""){
      if(res.result.status === 200){
        setGenMsg('Request marked as delivered')
        load()
      }else{
        setError(res.result.data?.message || 'Could not mark as delivered')
      }
    }else{
      setError(res.result?.data?.message || 'Network error')
    }
  }

  const formatDate = (iso) => { try { return new Date(iso).toLocaleString() } catch(e){ return iso } }

  return (
    <div className="mx-auto max-w-4xl px-3 sm:px-0">
      <div className="rounded-lg bg-white shadow-sm border border-gray-200">
        <div className="border-b border-gray-200 bg-gray-50 px-4 py-4 sm:px-6 sm:py-6">
          <h2 className="text-lg font-semibold text-gray-900 sm:text-2xl">Request Details</h2>
          <p className="mt-1 text-sm text-gray-600 break-words">
            Request ID: <span className="font-mono text-gray-900">{request?.requestId || postId}</span>
          </p>
          <p className="mt-1 text-sm text-gray-600">View request and submit your price</p>
        </div>
        <div className="p-4 sm:p-6">
          {loading ? (
            <div className="text-sm text-gray-500">Loading...</div>
          ) : error ? (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">{error}</div>
          ) : !request ? (
            <div className="text-sm text-gray-500">Request not found</div>
          ) : (
            <>
              {genMsg ? (
                <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3 text-sm font-medium text-green-700">{genMsg}</div>
              ) : null}
              <div className="mb-6">
                <div className="mb-2 flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">Status:</span>
                  <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                    request.status === 'PAID' ? 'bg-green-100 text-green-700' :
                    request.status === 'DELIVERED' ? 'bg-blue-100 text-blue-700' :
                    request.status === 'RECEIVED' ? 'bg-purple-100 text-purple-700' :
                    request.status === 'PRICED' ? 'bg-yellow-100 text-yellow-700' :
                    request.status === 'CANCELLED' ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {request.status}
                  </span>
                </div>
                <p className="mt-1 text-sm text-gray-700 whitespace-pre-line">{request.message}</p>
                <p className="mt-1 text-xs text-gray-400">Created: {formatDate(request.createdAt)}</p>
              </div>

              {/* Deliver button for hospitals when status is PAID */}
              {request.status === 'PAID' && request.selectedHospital && (String(request.selectedHospital._id || request.selectedHospital) === String(user?._id || user?.id)) ? (
                <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <button
                    onClick={handleMarkDelivered}
                    disabled={delivering}
                    className="w-full rounded-lg bg-black px-6 py-3 text-center text-sm font-semibold text-white transition-colors hover:bg-gray-900 disabled:cursor-not-allowed disabled:bg-gray-300"
                  >
                    {delivering ? 'Marking as Delivered...' : 'Mark as Delivered'}
                  </button>
                </div>
              ) : null}

              {myPrice ? (
                <div className="mb-6 rounded-lg border border-gray-200 p-4">
                  <h3 className="mb-2 text-sm font-semibold text-gray-900">Your price ({user?.organization || user?.name || 'Hospital'})</h3>
                  <p className="text-2xl font-bold text-gray-900">₦{Number(myPrice.price).toLocaleString()}</p>
                  {myPrice.comment ? <p className="mt-1 text-sm text-gray-600">{myPrice.comment}</p> : null}
                  <p className="mt-1 text-xs text-gray-400">Added: {formatDate(myPrice.createdAt)}</p>
                </div>
              ) : (
                <div className="mb-6 rounded-lg border border-gray-200 p-4">
                  <h3 className="mb-3 text-sm font-semibold text-gray-900">Submit a price</h3>
                  {genMsg ? <div className="mb-3 rounded-md border border-green-200 bg-green-50 p-2 text-xs font-medium text-green-700">{genMsg}</div> : null}
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="sm:col-span-1">
                      <label htmlFor="price" className="mb-1 block text-xs font-medium text-gray-900">Price (₦)</label>
                      <input id="price" value={price} onChange={(e)=>setPrice(e.target.value)} type="number" min="0" step="0.01" className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black" placeholder="0.00" />
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="comment" className="mb-1 block text-xs font-medium text-gray-900">Comment (optional)</label>
                      <input id="comment" value={comment} onChange={(e)=>setComment(e.target.value)} type="text" className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black" placeholder="e.g. includes consultation and delivery" />
                    </div>
                  </div>
                  <div className="mt-3">
                    <button onClick={()=>submitPrice()} type="button" className="w-full rounded-lg bg-black px-6 py-3 text-center text-sm font-semibold text-white transition-colors hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 sm:w-auto">{btnState}</button>
                  </div>
                </div>
              )}

              {/* Disputes for this request */}
              <div className="mb-6">
                <h3 className="mb-3 text-sm font-semibold text-gray-900">Disputes for this request</h3>
                {loadingDisputes ? (
                  <p className="text-sm text-gray-500">Loading disputes...</p>
                ) : disputes.length === 0 ? (
                  <p className="text-sm text-gray-500">No disputes submitted yet.</p>
                ) : (
                  <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200">
                    {disputes.map((dispute) => (
                      <li key={dispute._id} className="flex items-start justify-between gap-4 p-3 sm:p-4">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-gray-900">
                            {dispute.subject}
                          </p>
                          <p className="mt-1 text-xs text-gray-600 whitespace-pre-line line-clamp-3">
                            {dispute.message}
                          </p>
                          <p className="mt-1 text-xs text-gray-400">
                            Status:{" "}
                            <span className="font-medium">
                              {dispute.status === 'resolved' ? 'Resolved' : 'Open'}
                            </span>
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Dispute Form */}
              <DisputeForm postId={postId} />
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default HospitalRequestDetails


